<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="sub-fleet-page">
    <div class="page-header">
      <div class="page-header-text">
        <div class="page-eyebrow">Zarządzanie flotą</div>
        <h1 class="page-title">Podfloty</h1>
        <div class="page-subtitle">{{ $t('subFleet.subtitle') }}</div>
      </div>
      <button class="action-pill action-pill--primary" @click="openSubFleetDialog()">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t('subFleet.newSubFleet') }}
      </button>
    </div>

    <div class="summary-grid">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue"><v-icon>mdi-garage-variant</v-icon></div>
        <div><div class="summary-label">Wszystkie podfloty</div><div class="summary-value">{{ subFleets.length }}</div></div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green"><v-icon>mdi-check-circle-outline</v-icon></div>
        <div><div class="summary-label">Aktywne</div><div class="summary-value">{{ activeSubFleetsCount }}</div></div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--violet"><v-icon>mdi-car-multiple</v-icon></div>
        <div><div class="summary-label">Auta w podflotach</div><div class="summary-value">{{ totalCarsCount }}</div></div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--orange"><v-icon>mdi-pause-circle-outline</v-icon></div>
        <div><div class="summary-label">Wymagają uwagi</div><div class="summary-value">{{ inactiveSubFleetsCount }}</div></div>
      </div>
    </div>

    <!-- Protokoły właścicieli — zdalne wydania/zdania do akceptacji -->
    <v-card v-if="ownerProtocols.length" class="mb-4 content-card" outlined>
      <div class="table-toolbar">
        <div>
          <div class="section-title">
            Protokoły właścicieli
            <v-chip v-if="pendingProtocolsCount" x-small color="orange" dark class="ml-2">{{ pendingProtocolsCount }} do akceptacji</v-chip>
          </div>
          <div class="section-subtitle">Zdalne wydania i zwroty aut — najem rusza dopiero po Twojej akceptacji</div>
        </div>
        <button class="action-pill" @click="loadOwnerProtocols">
          <v-icon left small>mdi-refresh</v-icon>Odśwież
        </button>
      </div>
      <v-divider />
      <div class="pa-2">
        <div v-for="p in ownerProtocols" :key="p.id" class="owner-protocol-row" :class="'is-' + p.status.toLowerCase()">
          <div class="owner-protocol-row__head">
            <v-icon small :color="p.type === 'START' ? 'success' : 'error'" class="mr-2">
              {{ p.type === 'START' ? 'mdi-car-arrow-right' : 'mdi-car-arrow-left' }}
            </v-icon>
            <b>{{ p.car && p.car.plate }}</b>
            <span class="mx-1">—</span>
            {{ p.type === 'START' ? 'wydanie' : 'zwrot' }}
            · kierowca: <b class="ml-1">{{ p.driver && p.driver.firstName }} {{ p.driver && p.driver.lastName }}</b>
            <span v-if="p.driver && p.driver.phone" class="grey--text ml-1">({{ p.driver.phone }})</span>
            <v-spacer />
            <v-chip x-small dark :color="p.status === 'PENDING' ? 'orange' : p.status === 'APPROVED' ? 'success' : 'error'">
              {{ p.status === 'PENDING' ? 'Do akceptacji' : p.status === 'APPROVED' ? 'Zaakceptowany' : 'Odrzucony' }}
            </v-chip>
          </div>
          <div class="owner-protocol-row__meta">
            właściciel: {{ p.owner && p.owner.firstName }} {{ p.owner && p.owner.lastName }}
            · podflota: {{ p.subFleet && p.subFleet.name }}
            · przebieg: <b>{{ p.mileage }} km</b>
            <span v-if="p.car && p.car.odometer != null"> (ostatni znany: {{ p.car.odometer }} km)</span>
            · {{ formatDateTime(p.createdAt) }}
          </div>
          <div v-if="p.description" class="owner-protocol-row__meta">Uwagi: {{ p.description }}</div>
          <div v-if="p.status === 'REJECTED' && p.rejectReason" class="owner-protocol-row__meta error--text">Powód odrzucenia: {{ p.rejectReason }}</div>
          <div class="owner-protocol-row__files">
            <v-chip
              v-for="(ph, i) in p.photos"
              :key="ph.id"
              x-small outlined link class="mr-1 mb-1"
              @click="downloadProtocolPhoto(ph.id)"
            >
              <v-icon x-small left>mdi-image</v-icon>zdjęcie {{ i + 1 }}
            </v-chip>
            <v-chip v-if="p.driverSignature" x-small outlined class="mr-1 mb-1" @click="previewSignature(p, 'driver')">
              <v-icon x-small left>mdi-draw</v-icon>podpis kierowcy
            </v-chip>
            <v-chip v-if="p.ownerSignature" x-small outlined class="mb-1" @click="previewSignature(p, 'owner')">
              <v-icon x-small left>mdi-draw</v-icon>podpis właściciela
            </v-chip>
          </div>
          <div v-if="p.status === 'PENDING'" class="owner-protocol-row__actions">
            <v-btn small color="success" :loading="protocolActionId === p.id" @click="approveProtocol(p)">
              <v-icon left small>mdi-check</v-icon>Akceptuj
            </v-btn>
            <v-btn small outlined color="error" class="ml-2" :loading="protocolActionId === p.id" @click="rejectProtocol(p)">
              <v-icon left small>mdi-close</v-icon>Odrzuć
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <v-card class="mb-4 content-card" outlined>
      <div class="table-toolbar">
        <div>
          <div class="section-title">Lista podflot</div>
          <div class="section-subtitle">Dane właścicieli, opłaty i przypisane pojazdy</div>
        </div>
        <div class="table-filters">
          <v-text-field v-model="search" placeholder="Szukaj po nazwie lub właścicielu" prepend-inner-icon="mdi-magnify" hide-details outlined dense clearable class="search-field" />
          <v-select v-model="statusFilter" :items="statusFilterOptions" hide-details outlined dense class="status-filter" />
        </div>
      </div>
      <v-data-table
        :headers="headers"
        :items="filteredSubFleetRows"
        :loading="loading"
        class="elevation-0"
        no-data-text="Brak podflot"
        loading-text="Ładowanie podflot..."
        :footer-props="{ 'items-per-page-text': 'Wierszy na stronie', 'items-per-page-options': [10, 25, 50] }"
      >
        <template v-slot:item.name="{ item }">
          <button class="fleet-name-cell" @click="openSubFleetDetailDialog(item.id)">
            <span class="fleet-avatar">{{ fleetInitials(item.name) }}</span>
            <span><strong>{{ item.name }}</strong><small>{{ item.carsCount }} {{ carsLabel(item.carsCount) }}</small></span>
          </button>
        </template>
        <template v-slot:item.ownerName="{ item }">
          <div class="owner-cell"><v-icon small>mdi-account-outline</v-icon><span>{{ item.ownerName }}</span></div>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :class="'status-chip status-chip--' + item.status.toLowerCase()">
            <span class="status-dot"></span>{{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.feeType="{ item }">
          <span class="muted-cell">{{ feeTypeLabel(item.feeType) }}</span>
        </template>
        <template v-slot:item.feeSortValue="{ item }">
          <strong class="fee-cell">{{ formatFee(item) }}</strong>
        </template>
        <template v-slot:item.carsCount="{ item }">
          <span class="cars-count"><v-icon small>mdi-car-outline</v-icon>{{ item.carsCount }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="row-actions">
          <v-btn icon small @click="openSubFleetDetailDialog(item.id)" title="Szczegóły">
            <v-icon small>mdi-eye-outline</v-icon>
          </v-btn>
          <v-btn icon small @click="openSubFleetDialog(item)" title="Edytuj">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small class="delete-action" @click="deleteSubFleet(item.id)" title="Usuń">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / Edit dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="subFleetDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon left>{{ subFleetEdit.id ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ subFleetEdit.id ? $t('subFleet.editSubFleet') : $t('subFleet.newSubFleet') }}
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="subFleetEdit.name" label="Nazwa podfloty" outlined dense />
          <v-select
            v-model="subFleetEdit.parentId"
            :items="subFleetParentOptions"
            item-text="name"
            item-value="id"
            label="Nadrzędna podflota (opcjonalnie)"
            outlined
            dense
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="subFleetDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveSubFleet()">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="subFleetDetailDialog" max-width="900px" scrollable>
      <v-card v-if="subFleetDetail.id">
        <v-card-title>
          <v-icon left>mdi-garage-variant</v-icon>
          {{ subFleetDetail.name || 'Podflota' }}
          <v-spacer />
          <v-chip
            small
            :color="subFleetDetail.status === 'ACTIVE' ? 'success' : subFleetDetail.status === 'SUSPENDED' ? 'warning' : 'error'"
            text-color="white"
            class="mr-2"
          >
            {{ statusLabel(subFleetDetail.status) }}
          </v-chip>
          <v-btn icon @click="subFleetDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 70vh;">
          <v-tabs v-model="subFleetDetailTab" background-color="transparent" grow>
            <v-tab>
              <v-icon left small>mdi-account-outline</v-icon>{{ $t('subFleet.owner') }}
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-currency-usd</v-icon>{{ $t('subFleet.fees') }}
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-calculator-variant-outline</v-icon>{{ $t('subFleet.settlement') }}
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-car-multiple</v-icon>{{ $t('subFleet.carsAndDrivers') }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="subFleetDetailTab" touchless>
            <!-- Owner -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="subFleetDetail.ownerName" label="Imię i nazwisko właściciela" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="subFleetDetail.ownerCompanyName" label="Nazwa firmy" outlined dense />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field v-model="subFleetDetail.ownerAddress" label="Adres" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="subFleetDetail.ownerPhone" label="Telefon" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="subFleetDetail.ownerEmail" label="Email" outlined dense />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="subFleetDetail.ownerNip" label="NIP" outlined dense />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="subFleetDetail.ownerPesel" label="PESEL" outlined dense />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="subFleetDetail.ownerBankAccount" label="Nr konta bankowego" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select v-model="subFleetDetail.status" :items="subFleetStatuses" label="Status" outlined dense />
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="subFleetDetail.ownerDriverId"
                      :items="driverSelectOptions"
                      :loading="driverSelectLoading"
                      :search-input.sync="driverSearch"
                      :filter="noLocalFilter"
                      label="Właściciel jest kierowcą w systemie (opcjonalnie)"
                      hint="Jeśli wybierzesz, ten kierowca zobaczy zakładkę 'Moja podflota' w aplikacji kierowcy"
                      persistent-hint
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      clearable
                      hide-no-data
                      no-data-text="Brak wyników — wpisz fragment nazwiska/email"
                      prepend-inner-icon="mdi-account-link"
                    />
                  </v-col>
                </v-row>
                <v-btn color="primary" @click="saveSubFleetDetail()" :loading="subFleetDetailSaving">
                  <v-icon left small>mdi-content-save</v-icon>Zapisz
                </v-btn>
              </v-container>
            </v-tab-item>

            <!-- Fee -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select v-model="subFleetDetail.feeType" :items="feeTypeOptions" label="Typ opłaty" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.feeValue"
                      :label="'Wartość opłaty (' + feeValueSuffix + ')'"
                      type="number"
                      outlined
                      dense
                      :suffix="feeValueSuffix"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="subFleetDetail.feeType === 'MIXED'">
                    <v-text-field v-model="subFleetDetail.feeFixedBase" label="Stała kwota bazowa (PLN)" type="number" outlined dense suffix="PLN" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="subFleetDetail.feePeriod"
                      :items="feePeriodOptions"
                      label="Okres ryczałtu (prowizja firmy)"
                      hint="Za każdy taki okres należy się stawka opłaty od auta."
                      persistent-hint
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="subFleetDetail.payoutPeriod"
                      :items="feePeriodOptions"
                      label="Okres wypłaty właściciela"
                      hint="Co ile księgujemy właścicielowi pieniądze — niezależnie od tego, jak często naliczana jest prowizja."
                      persistent-hint
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="subFleetDetail.feeDayOfMonth" label="Dzień miesiąca" type="number" outlined dense min="1" max="28" />
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="subFleetDetail.costLimitMode"
                      :items="costLimitOptions"
                      label="Limit zwrotu kosztów właściciela"
                      hint="Kierowca odzyskuje z faktur najwyżej 8% netto z aplikacji. Właściciel podfloty zwykle nie jeździ, więc podstawą jest zebrany najem — albo limitu nie ma i liczy się to, co zaakceptuje biuro."
                      persistent-hint
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" v-if="subFleetDetail.feeType !== 'PERCENTAGE'">
                    <v-checkbox
                      v-model="subFleetDetail.feeProRata"
                      label="Prowizja proporcjonalna do dni najmu"
                      hint="Auto wynajęte 2 dni z 7 daje 2/7 opłaty zamiast pełnej stawki — tak jak czynsz kierowcy. Wyłączone: pełna stawka za każdy okres, w którym auto jeździło."
                      persistent-hint
                      dense
                      class="mt-0"
                    />
                  </v-col>
                </v-row>
                <v-btn color="primary" @click="saveSubFleetDetail()" :loading="subFleetDetailSaving">
                  <v-icon left small>mdi-content-save</v-icon>Zapisz
                </v-btn>
              </v-container>
            </v-tab-item>

            <!-- Settlement -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense align="center">
                  <v-col cols="12" md="3">
                    <v-select v-model="settlementPeriod" :items="feePeriodOptions" label="Okres" outlined dense />
                  </v-col>
                  <v-col v-if="settlementPeriod === 'MONTHLY'" cols="12" md="3">
                    <v-select v-model="settlementMonth" :items="monthOptions" label="Miesiąc" outlined dense />
                  </v-col>
                  <v-col v-else cols="12" md="3">
                    <v-text-field
                      v-model.number="settlementWeek"
                      :label="settlementPeriod === 'BIWEEKLY' ? 'Tydzień początkowy (ISO)' : 'Tydzień (ISO)'"
                      type="number" min="1" max="53" outlined dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field v-model="settlementYear" label="Rok" type="number" outlined dense />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-btn color="accent" @click="calculateSettlement()" :loading="settlementLoading">
                      <v-icon left small>mdi-calculator</v-icon>Oblicz
                    </v-btn>
                  </v-col>
                </v-row>
                <div v-if="settlementPeriodInfo" class="text-caption mb-2" style="color: #94A0B8;">
                  {{ settlementPeriodInfo }}
                </div>
                <v-card v-if="settlementData" outlined class="mt-4 pa-4">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('subFleet.totalRent') }}</div>
                      <div class="text-h6 success--text">{{ formatCurrency(settlementData.totalRent) }}</div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('subFleet.btpCommission') }}</div>
                      <div class="text-h6 warning--text">{{ formatCurrency(settlementData.btpFee) }}</div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('subFleet.ownerPayout') }}</div>
                      <div class="text-h6 accent--text">{{ formatCurrency(settlementData.netPayout) }}</div>
                    </v-col>
                  </v-row>
                  <v-divider class="my-3" />
                  <v-simple-table dense v-if="settlementData.details.length">
                    <thead>
                      <tr>
                        <th>{{ $t('subFleet.item') }}</th>
                        <th class="text-right">{{ $t('subFleet.amount') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(d, idx) in settlementData.details" :key="idx">
                        <td>{{ d.label }}</td>
                        <td class="text-right" :class="d.amount < 0 ? 'error--text' : ''">{{ formatCurrency(d.amount) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-container>
            </v-tab-item>

            <!-- Cars -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-data-table
                  :headers="subFleetCarsHeaders"
                  :items="subFleetDetailCars"
                  dense
                  class="elevation-1"
                  no-data-text="Brak przypisanych aut"
                />
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "SubFleetPage",
  data() {
    return {
      loading: false,
      subFleets: [],
      ownerProtocols: [],
      pendingProtocolsCount: 0,
      protocolActionId: null,
      search: "",
      statusFilter: "ALL",
      statusFilterOptions: [
        { text: "Wszystkie statusy", value: "ALL" },
        { text: "Aktywne", value: "ACTIVE" },
        { text: "Zawieszone", value: "SUSPENDED" },
        { text: "Zakończone", value: "ENDED" },
      ],
      headers: [
        { text: "Nazwa", value: "name", sortable: true },
        { text: "Właściciel", value: "ownerName", sortable: true },
        { text: "Typ opłaty", value: "feeType", sortable: true },
        { text: "Opłata", value: "feeSortValue", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Auta", value: "carsCount", align: "center", sortable: true },
        { text: "", value: "actions", sortable: false, align: "end", width: 150 },
      ],
      subFleetDialog: false,
      subFleetEdit: { id: null, name: "", parentId: null },
      subFleetDetailDialog: false,
      subFleetDetail: {},
      subFleetDetailTab: 0,
      subFleetDetailSaving: false,
      subFleetDetailCars: [],
      subFleetCarsHeaders: [
        { text: "Rejestracja", value: "plate" },
        { text: "Marka", value: "make" },
        { text: "Model", value: "model" },
        { text: "VIN", value: "vin" },
      ],
      subFleetStatuses: [
        { text: "Aktywna", value: "ACTIVE" },
        { text: "Zawieszona", value: "SUSPENDED" },
        { text: "Zakończona", value: "ENDED" },
      ],
      feeTypeOptions: [
        { text: "Procent", value: "PERCENTAGE" },
        { text: "Stała kwota", value: "FIXED" },
        { text: "Mieszana", value: "MIXED" },
      ],
      feePeriodOptions: [
        { text: "Tygodniowy", value: "WEEKLY" },
        { text: "Dwutygodniowy", value: "BIWEEKLY" },
        { text: "Miesięczny", value: "MONTHLY" },
      ],
      costLimitOptions: [
        { text: "Limit 8% od zebranego najmu", value: "PERCENT_OF_RENT" },
        { text: "Bez limitu — ile zaakceptuje biuro", value: "OFFICE_APPROVED" },
      ],
      monthOptions: [
        { text: "Styczeń", value: 1 }, { text: "Luty", value: 2 }, { text: "Marzec", value: 3 },
        { text: "Kwiecień", value: 4 }, { text: "Maj", value: 5 }, { text: "Czerwiec", value: 6 },
        { text: "Lipiec", value: 7 }, { text: "Sierpień", value: 8 }, { text: "Wrzesień", value: 9 },
        { text: "Październik", value: 10 }, { text: "Listopad", value: 11 }, { text: "Grudzień", value: 12 },
      ],
      settlementYear: new Date().getFullYear(),
      settlementPeriod: "MONTHLY",
      settlementWeek: (() => {
        // bieżący tydzień ISO
        const d = new Date();
        const day = (d.getDay() + 6) % 7;
        d.setDate(d.getDate() - day + 3);
        const firstThu = new Date(d.getFullYear(), 0, 4);
        const fday = (firstThu.getDay() + 6) % 7;
        firstThu.setDate(firstThu.getDate() - fday + 3);
        return 1 + Math.round((d - firstThu) / (7 * 24 * 3600 * 1000));
      })(),
      settlementMonth: new Date().getMonth() + 1,
      settlementLoading: false,
      settlementResult: null,
      // Driver autocomplete
      driverSelectOptions: [],
      driverSelectLoading: false,
      driverSearch: '',
      driverSearchTimer: null,
    };
  },
  watch: {
    driverSearch(val) {
      if (this.driverSearchTimer) clearTimeout(this.driverSearchTimer);
      this.driverSearchTimer = setTimeout(() => this.searchDrivers(val), 300);
    },
  },
  computed: {
    activeSubFleetsCount() {
      return this.subFleets.filter(sf => (sf.status || "ACTIVE") === "ACTIVE").length;
    },
    inactiveSubFleetsCount() {
      return this.subFleets.length - this.activeSubFleetsCount;
    },
    totalCarsCount() {
      return this.subFleets.reduce((total, sf) => total + (sf._count?.cars ?? sf.cars?.length ?? 0), 0);
    },
    subFleetRows() {
      return this.subFleets.map(sf => {
        const feeType = sf.feeType || "PERCENTAGE";
        const feeValue = sf.feeValue || 0;
        const feeFixedBase = sf.feeFixedBase || 0;
        // numeric value for sorting: PERCENTAGE uses feeValue/100 (% as number),
        // FIXED/MIXED use total fixed amount in PLN (feeFixedBase + feeValue normalized)
        let feeSortValue;
        if (feeType === "PERCENTAGE") feeSortValue = feeValue / 100;
        else if (feeType === "FIXED") feeSortValue = feeValue / 100;
        else feeSortValue = (feeFixedBase + feeValue) / 100;
        const ownerFromDriver = sf.ownerDriver
          ? `${sf.ownerDriver.firstName || ""} ${sf.ownerDriver.lastName || ""}`.trim()
          : "";
        return {
          id: sf.id,
          name: sf.name,
          ownerName: sf.ownerName || ownerFromDriver || "—",
          feeType,
          feeValue,
          feeFixedBase,
          feeSortValue,
          status: sf.status || "ACTIVE",
          carsCount: sf._count?.cars ?? sf.cars?.length ?? 0,
        };
      });
    },
    subFleetParentOptions() {
      return this.subFleets
        .filter(sf => sf.id !== this.subFleetEdit.id)
        .map(sf => ({ id: sf.id, name: sf.name }));
    },
    filteredSubFleetRows() {
      const query = (this.search || "").trim().toLocaleLowerCase("pl-PL");
      return this.subFleetRows.filter(row => {
        const matchesStatus = this.statusFilter === "ALL" || row.status === this.statusFilter;
        const haystack = `${row.name || ""} ${row.ownerName || ""}`.toLocaleLowerCase("pl-PL");
        return matchesStatus && (!query || haystack.includes(query));
      });
    },
    feeValueSuffix() {
      return this.subFleetDetail.feeType === "PERCENTAGE" ? "%" : "PLN";
    },
    settlementData() {
      const r = this.settlementResult;
      if (!r) return null;
      const details = [];
      if (Array.isArray(r.perCar)) {
        for (const c of r.perCar) {
          details.push({ label: `Najem ${c.plate}`, amount: c.rentCollected });
          details.push({ label: `Prowizja ${c.plate}`, amount: -c.commission });
        }
      }
      return {
        totalRent: r.totalRentCollected || 0,
        btpFee: r.feeAmount || 0,
        netPayout: r.netPayoutToOwner || 0,
        details,
      };
    },
    settlementPeriodInfo() {
      const r = this.settlementResult;
      if (!r) return "";
      if (r.weekStart && r.weekEnd) {
        const f = (x) => new Date(x).toLocaleDateString("pl-PL");
        return `Policzony okres: ${f(r.weekStart)} – ${f(r.weekEnd)}`;
      }
      if (r.month && r.year) return `Policzony okres: ${r.month}.${r.year}`;
      return "";
    },
  },
  async mounted() {
    await this.fetchSubFleets();
    this.loadOwnerProtocols();
  },
  methods: {
    async loadOwnerProtocols() {
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'sub-fleet/owner-protocols',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.ownerProtocols = res.data?.data?.protocols || [];
        this.pendingProtocolsCount = res.data?.data?.pendingCount || 0;
      } catch (e) {
        console.error('Failed to load owner protocols:', e);
      }
    },
    formatDateTime(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
    },
    async downloadProtocolPhoto(fileId) {
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'aws/download-file/' + fileId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank');
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się pobrać zdjęcia', 'error');
      }
    },
    previewSignature(p, who) {
      const sig = who === 'driver' ? p.driverSignature : p.ownerSignature;
      if (!sig) return;
      Swal.fire({
        title: who === 'driver' ? 'Podpis kierowcy' : 'Podpis właściciela',
        imageUrl: sig,
        imageAlt: 'podpis',
        background: '#fff',
      });
    },
    async approveProtocol(p) {
      const res = await Swal.fire({
        icon: 'question',
        title: 'Zaakceptować protokół?',
        html: `Auto <b>${p.car?.plate || ''}</b> — ${p.type === 'START' ? 'WYDANIE' : 'ZWROT'}, kierowca <b>${p.driver?.firstName || ''} ${p.driver?.lastName || ''}</b>, przebieg <b>${p.mileage} km</b>.<br><br>${p.type === 'START' ? 'Kierowca zostanie przypisany do auta i najem wystartuje w rozliczeniach.' : 'Kierowca zostanie odpięty od auta i najem zakończy się w rozliczeniach.'}`,
        showCancelButton: true,
        confirmButtonText: 'Akceptuj',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#2e7d32',
      });
      if (!res.isConfirmed) return;
      this.protocolActionId = p.id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'sub-fleet/owner-protocols/' + p.id + '/approve',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        await Promise.all([this.loadOwnerProtocols(), this.fetchSubFleets()]);
        // Wydanie auta = dobry moment na kaucję (trzymaną przez BTP).
        if (p.type === 'START') {
          const dep = await Swal.fire({
            icon: 'success',
            title: 'Protokół zaakceptowany',
            html: `Najem wystartował. Ustawić <b>kaucję</b> dla kierowcy ${p.driver?.firstName || ''} ${p.driver?.lastName || ''}? Raty zejdą z wypłat automatycznie.`,
            showCancelButton: true,
            confirmButtonText: 'Ustaw kaucję',
            cancelButtonText: 'Bez kaucji',
          });
          if (dep.isConfirmed) {
            const { value, isConfirmed } = await Swal.fire({
              title: 'Kaucja',
              html:
                '<input id="sf-dep-amount" type="number" min="1" step="0.01" class="swal2-input" placeholder="Kwota kaucji (zł), np. 1000">' +
                '<select id="sf-dep-mode" class="swal2-select" style="width:80%">' +
                '<option value="INSTALLMENTS">Zbierana ratami z wypłat</option>' +
                '<option value="UPFRONT">Wpłacona jednorazowo przy wydaniu</option>' +
                '</select>' +
                '<input id="sf-dep-inst" type="number" min="1" max="52" class="swal2-input" placeholder="Liczba rat tygodniowych, np. 4">' +
                '<select id="sf-dep-method" class="swal2-select" style="width:80%;display:none">' +
                '<option value="CASH">Gotówka</option>' +
                '<option value="TRANSFER">Przelew</option>' +
                '</select>',
              focusConfirm: false,
              showCancelButton: true,
              confirmButtonText: 'Zapisz',
              cancelButtonText: 'Anuluj',
              didOpen: () => {
                const mode = document.getElementById('sf-dep-mode');
                const inst = document.getElementById('sf-dep-inst');
                const method = document.getElementById('sf-dep-method');
                const przelacz = () => {
                  const upfront = mode.value === 'UPFRONT';
                  inst.style.display = upfront ? 'none' : '';
                  method.style.display = upfront ? '' : 'none';
                };
                mode.addEventListener('change', przelacz);
                przelacz();
              },
              preConfirm: () => ({
                amount: parseFloat(document.getElementById('sf-dep-amount').value),
                upfront: document.getElementById('sf-dep-mode').value === 'UPFRONT',
                installments: parseInt(document.getElementById('sf-dep-inst').value, 10),
                method: document.getElementById('sf-dep-method').value,
              }),
            });
            if (isConfirmed && value.amount > 0 && (value.upfront || value.installments >= 1)) {
              try {
                await axios({
                  method: 'post',
                  url: process.env.VUE_APP_ROOT_API + 'deposits',
                  data: {
                    driverId: p.driver?.id,
                    carId: p.car?.id,
                    totalAmount: Math.round(value.amount * 100),
                    installments: value.upfront ? 1 : value.installments,
                    paidUpfront: value.upfront || undefined,
                    paidUpfrontMethod: value.upfront ? value.method : undefined,
                    note: `Kaucja przy wydaniu ${p.car?.plate || ''} (protokół właściciela)`,
                  },
                  headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
                });
                Swal.fire({
                  icon: 'success',
                  title: value.upfront ? 'Kaucja zapisana jako wpłacona' : 'Kaucja ustawiona',
                  timer: 1800,
                  showConfirmButton: false,
                });
              } catch (e) {
                Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
              }
            }
          }
        } else {
          Swal.fire({ icon: 'success', title: 'Protokół zaakceptowany', timer: 1600, showConfirmButton: false });
        }
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      } finally {
        this.protocolActionId = null;
      }
    },
    async rejectProtocol(p) {
      const { value: reason, isConfirmed } = await Swal.fire({
        title: 'Powód odrzucenia',
        input: 'textarea',
        inputPlaceholder: 'np. nieczytelne zdjęcia, zły przebieg…',
        inputAttributes: { maxlength: 500 },
        showCancelButton: true,
        confirmButtonText: 'Odrzuć protokół',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#d33',
      });
      if (!isConfirmed || !reason?.trim()) return;
      this.protocolActionId = p.id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'sub-fleet/owner-protocols/' + p.id + '/reject',
          data: { reason: reason.trim() },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        await this.loadOwnerProtocols();
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      } finally {
        this.protocolActionId = null;
      }
    },
    fleetInitials(name) {
      return (name || "P").split(/\s+/).filter(Boolean).slice(0, 2)
        .map(part => part.charAt(0)).join("").toUpperCase();
    },
    carsLabel(count) {
      if (count === 1) return "auto";
      if (count >= 2 && count <= 4) return "auta";
      return "aut";
    },
    statusLabel(s) {
      return ({ ACTIVE: "Aktywna", SUSPENDED: "Zawieszona", ENDED: "Zakończona" }[s]) || s || "—";
    },
    feeTypeLabel(t) {
      return ({ PERCENTAGE: "Procent", FIXED: "Stała", MIXED: "Mieszana" }[t]) || "—";
    },
    formatFee(item) {
      if (item.feeType === "PERCENTAGE") return `${(item.feeValue || 0) / 100}%`;
      if (item.feeType === "FIXED") return `${((item.feeValue || 0) / 100).toFixed(2)} PLN`;
      if (item.feeType === "MIXED") return `${((item.feeFixedBase || 0) / 100).toFixed(2)} PLN + ${(item.feeValue || 0) / 100}%`;
      return "—";
    },
    formatCurrency(v) {
      return `${((v || 0) / 100).toFixed(2)} PLN`;
    },
    authHeaders() {
      return { Authorization: "Bearer " + localStorage.getItem("token") };
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },

    async fetchSubFleets() {
      this.loading = true;
      try {
        const res = await axios.get(this.apiUrl("sub-fleet"), { headers: this.authHeaders() });
        this.subFleets = res.data?.data || [];
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },

    openSubFleetDialog(item) {
      if (item) {
        this.subFleetEdit = { id: item.id, name: item.name, parentId: item.parentId || null };
      } else {
        this.subFleetEdit = { id: null, name: "", parentId: null };
      }
      this.subFleetDialog = true;
    },
    async saveSubFleet() {
      if (!this.subFleetEdit.name?.trim()) {
        Swal.fire("Błąd", "Nazwa podfloty jest wymagana", "error");
        return;
      }
      try {
        if (this.subFleetEdit.id) {
          await axios.patch(
            this.apiUrl("sub-fleet/" + this.subFleetEdit.id),
            { name: this.subFleetEdit.name },
            { headers: this.authHeaders() },
          );
        } else {
          await axios.post(
            this.apiUrl("sub-fleet"),
            { name: this.subFleetEdit.name, parentId: this.subFleetEdit.parentId || undefined },
            { headers: this.authHeaders() },
          );
        }
        this.subFleetDialog = false;
        await this.fetchSubFleets();
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Błąd zapisu";
        Swal.fire("Błąd", msg, "error");
      }
    },
    async deleteSubFleet(id) {
      const res = await Swal.fire({
        title: "Usunąć podflotę?",
        text: "Auta zostaną odpięte z podfloty",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj",
      });
      if (!res.isConfirmed) return;
      try {
        await axios.delete(this.apiUrl("sub-fleet/" + id), { headers: this.authHeaders() });
        await this.fetchSubFleets();
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Błąd";
        Swal.fire("Błąd", msg, "error");
      }
    },

    async openSubFleetDetailDialog(id) {
      try {
        const res = await axios.get(this.apiUrl("sub-fleet/" + id + "/details"), { headers: this.authHeaders() });
        const payload = res.data?.data || res.data || {};
        // Konwersja groszy z backendu → PLN dla UI (PERCENTAGE też: 1000 → 10%)
        this.subFleetDetail = {
          ...payload,
          feeValue: (payload.feeValue || 0) / 100,
          feeFixedBase: (payload.feeFixedBase || 0) / 100,
        };
        this.subFleetDetailCars = (payload.cars || []).map(car => ({
          ...car,
          driverName: car.drivers?.[0] ? `${car.drivers[0].firstName} ${car.drivers[0].lastName}` : "-",
        }));
        this.subFleetDetailTab = 0;
        this.settlementResult = null;
        // Rozliczenie domyślnie w okresie ustawionym dla podfloty
        this.settlementPeriod = payload.feePeriod || "MONTHLY";
        this.driverSelectOptions = [];
        // Jeśli już jest przypisany ownerDriver — wczytaj jego dane do autocomplete-a
        if (payload.ownerDriverId && payload.ownerDriver) {
          this.driverSelectOptions = [{
            id: payload.ownerDriver.id,
            name: `${payload.ownerDriver.firstName} ${payload.ownerDriver.lastName} (${payload.ownerDriver.email || ''})`,
          }];
        }
        this.subFleetDetailDialog = true;
        // Załaduj pierwsze 50 kierowców żeby user widział listę bez wpisywania
        this.loadInitialDrivers();
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Nie można załadować podfloty";
        Swal.fire("Błąd", msg, "error");
      }
    },
    async saveSubFleetDetail() {
      this.subFleetDetailSaving = true;
      try {
        // Konwersja PLN (UI) → grosze (backend)
        const feeValuePLN = parseFloat(this.subFleetDetail.feeValue) || 0;
        const feeFixedBasePLN = parseFloat(this.subFleetDetail.feeFixedBase) || 0;
        await axios.patch(
          this.apiUrl("sub-fleet/" + this.subFleetDetail.id),
          {
            ownerName: this.subFleetDetail.ownerName,
            ownerCompanyName: this.subFleetDetail.ownerCompanyName,
            ownerAddress: this.subFleetDetail.ownerAddress,
            ownerPhone: this.subFleetDetail.ownerPhone,
            ownerEmail: this.subFleetDetail.ownerEmail,
            ownerNip: this.subFleetDetail.ownerNip,
            ownerPesel: this.subFleetDetail.ownerPesel,
            ownerBankAccount: this.subFleetDetail.ownerBankAccount,
            ownerDriverId: this.subFleetDetail.ownerDriverId || null,
            status: this.subFleetDetail.status,
            feeType: this.subFleetDetail.feeType,
            feeValue: Math.round(feeValuePLN * 100),
            feeFixedBase: Math.round(feeFixedBasePLN * 100),
            feePeriod: this.subFleetDetail.feePeriod,
            payoutPeriod: this.subFleetDetail.payoutPeriod || this.subFleetDetail.feePeriod,
            feeDayOfMonth: parseInt(this.subFleetDetail.feeDayOfMonth) || 1,
            feeProRata: !!this.subFleetDetail.feeProRata,
            costLimitMode: this.subFleetDetail.costLimitMode || "PERCENT_OF_RENT",
          },
          { headers: this.authHeaders() },
        );
        Swal.fire("Zapisano", "Dane podfloty zaktualizowane", "success");
        this.fetchSubFleets();
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Błąd";
        Swal.fire("Błąd", msg, "error");
      }
      this.subFleetDetailSaving = false;
    },

    noLocalFilter() {
      // wyłączamy lokalne filtrowanie Vuetify — filtruje backend po `search`
      return true;
    },
    async loadInitialDrivers() {
      this.driverSelectLoading = true;
      try {
        const res = await axios.get(
          this.apiUrl(`drivers?take=50&skip=1&search=`),
          { headers: this.authHeaders() },
        );
        const list = res.data?.data?.result || [];
        const newOptions = list.map(d => ({
          id: d.id,
          name: `${d.firstName} ${d.lastName} (${d.email || d.phone || ''})`,
        }));
        // zachowaj już-wybranego ownera jeśli był załadowany w openSubFleetDetailDialog
        const existing = this.driverSelectOptions.filter(o =>
          !newOptions.find(n => n.id === o.id)
        );
        this.driverSelectOptions = [...existing, ...newOptions];
      } catch (e) {
        console.error('loadInitialDrivers:', e);
      }
      this.driverSelectLoading = false;
    },
    async searchDrivers(q) {
      // brak query → załaduj domyślną listę
      if (!q || q.length < 2) {
        if (!q) await this.loadInitialDrivers();
        return;
      }
      this.driverSelectLoading = true;
      try {
        const res = await axios.get(
          this.apiUrl(`drivers?take=50&skip=1&search=${encodeURIComponent(q)}`),
          { headers: this.authHeaders() },
        );
        const list = res.data?.data?.result || [];
        const newOptions = list.map(d => ({
          id: d.id,
          name: `${d.firstName} ${d.lastName} (${d.email || d.phone || ''})`,
        }));
        const existing = this.driverSelectOptions.filter(o =>
          !newOptions.find(n => n.id === o.id) && o.id === this.subFleetDetail.ownerDriverId
        );
        this.driverSelectOptions = [...existing, ...newOptions];
      } catch (e) {
        console.error('searchDrivers:', e);
      }
      this.driverSelectLoading = false;
    },
    async calculateSettlement() {
      this.settlementLoading = true;
      try {
        let path;
        if (this.settlementPeriod === "WEEKLY") {
          path = `sub-fleet/${this.subFleetDetail.id}/settlement-weekly?year=${this.settlementYear}&week=${this.settlementWeek}`;
        } else if (this.settlementPeriod === "BIWEEKLY") {
          path = `sub-fleet/${this.subFleetDetail.id}/settlement-biweekly?year=${this.settlementYear}&week=${this.settlementWeek}`;
        } else {
          path = `sub-fleet/${this.subFleetDetail.id}/settlement?year=${this.settlementYear}&month=${this.settlementMonth}`;
        }
        const res = await axios.get(this.apiUrl(path), { headers: this.authHeaders() });
        this.settlementResult = res.data?.data || res.data;
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || "Błąd rozliczenia";
        Swal.fire("Błąd", msg, "error");
      }
      this.settlementLoading = false;
    },
  },
};
</script>

<style scoped>
.sub-fleet-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 30px;
  line-height: 1.15;
  font-weight: 750;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.page-subtitle {
  margin-top: 6px;
  color: #94A0B8;
  font-size: 13px;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary {
  background: linear-gradient(135deg, #5478FF, #6C63FF);
  color: #FFFFFF;
  box-shadow: 0 8px 22px rgba(84, 120, 255, 0.25);
}
.action-pill--primary:hover { transform: translateY(-1px); box-shadow: 0 10px 26px rgba(84, 120, 255, 0.34); }
.action-pill--primary .v-icon { color: #fff !important; }
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.summary-card {
  min-height: 104px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(145deg, #252B3C, #212636);
  border: 1px solid #30374A;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(5, 8, 20, 0.12);
}
.summary-icon {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: grid;
  place-items: center;
  border-radius: 12px;
}
.summary-icon .v-icon { font-size: 23px; }
.summary-icon--blue { background: rgba(84,120,255,.15); }
.summary-icon--blue .v-icon { color: #7693FF; }
.summary-icon--green { background: rgba(65,196,135,.14); }
.summary-icon--green .v-icon { color: #4FD399; }
.summary-icon--violet { background: rgba(154,112,255,.14); }
.summary-icon--violet .v-icon { color: #A984FF; }
.summary-icon--orange { background: rgba(255,169,77,.14); }
.summary-icon--orange .v-icon { color: #FFB15C; }
.summary-label { color: #94A0B8; font-size: 12px; line-height: 1.3; }
.summary-value { color: #fff; font-size: 25px; line-height: 1.2; font-weight: 750; margin-top: 3px; }
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-color: #2A3142 !important;
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(5, 8, 20, 0.14) !important;
}
.table-toolbar {
  min-height: 82px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid #30374A;
}
.section-title { color: #fff; font-size: 16px; font-weight: 700; }
.section-subtitle { color: #7F8AA3; font-size: 12px; margin-top: 3px; }
.table-filters { display: flex; gap: 10px; align-items: center; }
.search-field { width: 280px; }
.status-filter { width: 180px; }
.sub-fleet-page >>> .table-filters .v-input__slot {
  min-height: 40px !important;
  background: #1D2230 !important;
  border-radius: 9px !important;
}
.sub-fleet-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.sub-fleet-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
  height: 43px !important;
  text-transform: uppercase;
  letter-spacing: .045em;
  font-size: 10px !important;
  font-weight: 700;
}
.sub-fleet-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
  height: 68px !important;
}
.sub-fleet-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.fleet-name-cell {
  display: flex;
  align-items: center;
  gap: 11px;
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.fleet-name-cell strong { display: block; font-size: 13px; }
.fleet-name-cell small { display: block; color: #7F8AA3; font-size: 11px; margin-top: 2px; }
.fleet-avatar {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  flex: 0 0 36px;
  color: #91A6FF;
  background: linear-gradient(145deg, rgba(84,120,255,.22), rgba(108,99,255,.12));
  border: 1px solid rgba(117,143,255,.22);
  border-radius: 10px;
  font-weight: 750;
  font-size: 11px;
}
.owner-cell { display: flex; align-items: center; gap: 7px; color: #C8CFDE; }
.owner-cell .v-icon { color: #66728B !important; }
.muted-cell { color: #AAB3C6; }
.fee-cell { color: #E8ECF5; font-size: 12px; white-space: nowrap; }
.cars-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 48px;
  padding: 5px 9px;
  color: #C8D1E4;
  background: #1D2230;
  border: 1px solid #30374A;
  border-radius: 8px;
}
.cars-count .v-icon { color: #71809C !important; }
.status-chip {
  height: 25px !important;
  border-radius: 999px !important;
  font-size: 10px !important;
  font-weight: 700;
  letter-spacing: .02em;
}
.status-chip--active { color: #62DEA8 !important; background: rgba(65,196,135,.12) !important; }
.status-chip--suspended { color: #FFC06E !important; background: rgba(255,169,77,.12) !important; }
.status-chip--ended { color: #FF8990 !important; background: rgba(255,99,109,.12) !important; }
.status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; margin-right: 6px; }
.row-actions { display: flex; justify-content: flex-end; gap: 2px; }
.row-actions .v-btn { color: #7F8AA3 !important; background: transparent; }
.row-actions .v-btn:hover { color: #AFC0FF !important; background: rgba(84,120,255,.1); }
.row-actions .delete-action:hover { color: #FF7B83 !important; background: rgba(255,99,109,.1); }
.sub-fleet-page >>> .v-data-footer {
  color: #94A0B8 !important;
  border-top: 1px solid #30374A !important;
}
.sub-fleet-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.sub-fleet-page >>> .v-text-field input,
.sub-fleet-page >>> .v-text-field textarea,
.sub-fleet-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.sub-fleet-page >>> .v-label {
  color: #94A0B8 !important;
}
.sub-fleet-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.sub-fleet-page >>> .v-card__title,
.sub-fleet-page >>> .v-card__text {
  color: #E6EAF2 !important;
}
.sub-fleet-page >>> .v-tab {
  color: #94A0B8 !important;
}
.sub-fleet-page >>> .v-tab--active {
  color: #5478FF !important;
}
.sub-fleet-page >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
@media (max-width: 1100px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .table-toolbar { align-items: flex-start; flex-direction: column; }
  .table-filters { width: 100%; }
  .search-field { flex: 1; width: auto; }
}
@media (max-width: 700px) {
  .sub-fleet-page { margin: -12px; padding: 16px 12px; }
  .page-header { align-items: flex-start; flex-direction: column; }
  .action-pill { width: 100%; justify-content: center; }
  .summary-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .summary-card { min-height: 88px; padding: 12px; gap: 10px; }
  .summary-icon { width: 38px; height: 38px; flex-basis: 38px; }
  .summary-label { font-size: 10px; }
  .summary-value { font-size: 21px; }
  .table-filters { align-items: stretch; flex-direction: column; }
  .search-field, .status-filter { width: 100%; }
}
/* Protokoły właścicieli */
.owner-protocol-row {
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  font-size: 13px;
}
.owner-protocol-row.is-pending { border-color: rgba(255, 167, 38, 0.35); background: rgba(255, 167, 38, 0.05); }
.owner-protocol-row__head { display: flex; align-items: center; flex-wrap: wrap; }
.owner-protocol-row__meta { margin-top: 3px; color: #97a3b8; font-size: 12px; }
.owner-protocol-row__files { margin-top: 7px; }
.owner-protocol-row__actions { margin-top: 9px; }
</style>
